/**
 * saveOutcome — how to read an `apiClient` result on a write.
 *
 * `apiClient` (helper/api.ts) NEVER THROWS, and a write can fail in three different shapes. Only one
 * shape is a success, and each of the other two has been shipped as a silent success at least once:
 *
 *   1. HTTP ok        -> the parsed, camel-cased JSON body (or the raw `Response` for file endpoints)
 *   2. HTTP not-ok    -> `{ ok: false, status, statusText, url, ...json }`
 *   3. fetch REJECTED -> `undefined`, after reporting to Sentry (offline, DNS, CORS, TLS, a connection
 *                        dropped mid-request)
 *
 * ...plus a fourth case that is NOT an apiClient shape but a payload one: the API's own
 * `failedResponse()` (lib/utils/responseService.js) builds `{ status: false, message, ... }`, and a
 * handler that returns it WITHOUT also setting an HTTP error status delivers a business-level failure
 * at HTTP 200. That is why the check below is three-armed rather than two — it is the idiom already
 * used across this codebase (see stateFilingCredentials.ts and the Carrier/InsuranceCompany lists).
 *
 * The obvious test — `Boolean(res && res.ok === false)` — misses cases 3 and 4, so a write that never
 * reached the server, or that the server explicitly refused, reports to the user as saved. On a filing
 * screen that is a silently lost regulatory correction.
 *
 * Note this checks `res == null` rather than the shorter `!res` used at some call sites: a 2xx body
 * that is legitimately `0`, `''` or `false` is a SUCCESS and must not be read as a failure.
 */
export function isSaveFailure(res: unknown): boolean {
    // No response at all: fetch itself rejected. A write we cannot confirm must be treated as failed —
    // the safe default is "your correction may not have saved", never the reverse.
    if (res == null) return true;
    const r = res as { ok?: unknown; status?: unknown };
    // The error envelope apiClient builds for a non-2xx response.
    if (r.ok === false) return true;
    // A `failedResponse()` body that arrived with an HTTP 200. Compared strictly to `false` so a
    // numeric HTTP `status` (e.g. 200 on a raw Response) is never mistaken for a failure.
    return r.status === false;
}
